import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { ApcMark } from '../components/ApcMark';

/**
 * Handles the `#session_id=…` fragment returned by Emergent OAuth.
 * Mounted by the router when `location.hash` contains a session_id — before
 * any ProtectedRoute runs — so the session is established first.
 */
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const match = location.hash?.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    (async () => {
      if (!sessionId) {
        setError('Missing session token.');
        return;
      }
      try {
        await api.post('/auth/session', { session_id: sessionId });
        // Clear the fragment so refreshes do not retry the one-time token.
        window.history.replaceState(null, '', '/admin');
        await refresh();
        navigate('/admin', { replace: true });
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-5" data-testid="auth-callback">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <ApcMark size={30}/><span className="font-bold" style={{ fontFamily: 'Outfit' }}>APC Admin</span>
        </div>
        {!error ? (
          <div className="bg-white rounded-3xl border border-black/5 p-8">
            <div className="w-10 h-10 border-4 border-[#1F6F4A] border-t-transparent rounded-full animate-spin mx-auto mb-4"/>
            <p className="text-sm text-[#4B5563]">Finishing sign-in…</p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-black/5 p-8 text-left">
            <h1 className="text-xl font-bold text-[#1B2A41] mb-2" style={{ fontFamily: 'Outfit' }}>Sign-in rejected</h1>
            <p className="text-sm text-[#4B5563] mb-5" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => (window.location.href = '/admin/login')}
              className="btn-pill btn-primary w-full justify-center"
              data-testid="auth-callback-back"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
